import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { FaClock, FaExclamationTriangle, FaCheckCircle, FaCalendarAlt } from 'react-icons/fa';
import '../styles/DueEvents.css';
import LoadingSpinner from './LoadingSpinner';

const DueEvents = ({ userId, userRole = 'user' }) => {
    const [dueEvents, setDueEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    useEffect(() => {
        if (userId) {
            fetchDueEvents();
        }
    }, [userId]);

    const fetchDueEvents = async () => {
        try {
            setLoading(true);
            setError('');
            
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🔍 Fetching due events for user:', userId);
            const response = await axiosInstance.get(`/notifications/due_events/${userId}`);
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('📋 Due events response:', response.data);
            setDueEvents(response.data || []);
            
        } catch (err) {
            console.error('❌ Failed to fetch due events:', err);
            setError('Failed to load due events');
        } finally {
            setLoading(false);
        }
    };

    const getEventIcon = (notificationType) => {
        switch (notificationType) {
            case 'attendance_request':
                return <FaClock className="event-icon attendance-icon" />;
            case 'evaluation_request':
                return <FaExclamationTriangle className="event-icon evaluation-icon" />;
            case 'schedule_update':
                return <FaCalendarAlt className="event-icon schedule-icon" />;
            default:
                return <FaExclamationTriangle className="event-icon default-icon" />;
        }
    };

    const getEventTitle = (notificationType) => {
        switch (notificationType) {
            case 'attendance_request':
                return 'Attendance Request';
            case 'evaluation_request':
                return 'Evaluation Request';
            case 'schedule_update':
                return 'Schedule Update';
            default:
                return 'Pending Action';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No date';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    };

    const handleEventAction = async (eventId, action) => {
        try {
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`🔄 Handling ${action} for event:`, eventId);
            const response = await axiosInstance.post(`/notifications/action/${eventId}`, {
                action: action
            });
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('✅ Action completed:', response.data);
            
            // Refresh the due events
            fetchDueEvents();
            
        } catch (err) {
            console.error('❌ Failed to handle event action:', err);
            setError(`Failed to ${action} the request`);
        }
    };

    if (loading) {
        return (
            <div className="due-events-container">
                <div className="due-events-header">
                    <h2 className="card-title due-events-title">
                        <FaClock className="title-icon" />
                        Due Events
                    </h2>
                </div>
                <div className="due-events-loading">
                    <LoadingSpinner text="Loading due events..." variant="inline" />
                </div>
            </div>
        );
    }

    return (
        <div className="due-events-container">
            <div className="due-events-header">
                <h2 className="card-title due-events-title">
                    <FaClock className="title-icon" />
                    Due Events
                    {dueEvents.length > 0 && (
                        <span className="due-count-badge">{dueEvents.length}</span>
                    )}
                </h2>
                {error && <p className="error-text">{error}</p>}
            </div>

            <div className="due-events-content">
                {dueEvents.length === 0 ? (
                    <div className="no-events-state">
                        <FaCheckCircle className="no-events-icon" />
                        <h4>All caught up!</h4>
                        <p>No due events at this time</p>
                    </div>
                ) : (
                    <div className="events-list">
                        {dueEvents.map((event) => (
                            <div key={event._id} className="event-card">
                                <div className="event-main-content">
                                    <div className="event-header">
                                        {getEventIcon(event.notificationType)}
                                        <div className="event-details">
                                            <h4 className="event-title">
                                                {getEventTitle(event.notificationType)}
                                            </h4>
                                            <p className="event-message">
                                                {event.message || 'Action required'}
                                            </p>
                                            <span className="event-date">
                                                Created: {formatDate(event.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {event.isActionBased && !event.actionStatus && (
                                        <div className="event-actions">
                                            {event.notificationType === 'attendance_request' && userRole === 'preceptor' && (
                                                <>
                                                    <button 
                                                        className="action-button approve-button"
                                                        onClick={() => handleEventAction(event._id, 'approve')}
                                                    >
                                                        Approve
                                                    </button>
                                                    <button 
                                                        className="action-button reject-button"
                                                        onClick={() => handleEventAction(event._id, 'reject')}
                                                    >
                                                        Reject
                                                    </button>
                                                </>
                                            )}
                                            {event.notificationType === 'evaluation_request' && (
                                                <button 
                                                    className="action-button primary-button"
                                                    onClick={() => handleEventAction(event._id, 'complete')}
                                                >
                                                    Complete Evaluation
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DueEvents;
